import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import NoIndex from "@/components/NoIndex";
import Teaser from "@/components/Teaser";
import ReportView from "@/components/ReportView";
import NotEligible from "@/components/NotEligible";
import { API, track } from "@/lib/okkio";
import { metaInitiateCheckout, metaQuoteAnalyzedOnce } from "@/lib/metaPixel";

export default function Report() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/report/${token}`);
      setData(res.data);
      const st = res.data.status;
      if (st && st !== "done" && st !== "not_eligible") {
        setTimeout(load, 3000);
      }
    } catch {
      setError(true);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Meta Pixel custom event: SOLO quando il teaser (analisi completata) è mostrato.
  // Idempotente per token e consent-gated (guard interno a metaQuoteAnalyzedOnce).
  useEffect(() => {
    if (data && data.status === "done" && !data.paid) metaQuoteAnalyzedOnce(token);
  }, [data, token]);

  const onUnlock = async () => {
    setUnlocking(true);
    try {
      const { data: res } = await axios.post(`${API}/payments/checkout`, {
        token, origin_url: window.location.origin,
      });
      track("checkout_start", { token });
      metaInitiateCheckout();
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Impossibile avviare il pagamento. Riprova.");
      setUnlocking(false);
    }
  };

  if (error) return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <NoIndex />
      <header className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="nav-logo" className="flex items-center"><Logo className="text-2xl" /></button>
        <button onClick={() => navigate("/")} data-testid="back-home"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white px-4 h-10 text-sm font-semibold text-[#334155] hover:border-[#3157F6] hover:text-[#3157F6] transition-colors">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Torna alla Home
        </button>
      </header>
      <div className="flex items-center justify-center px-6 text-center pt-24">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Report non trovato</h1>
          <p className="mt-2 text-slate-500">Il link potrebbe essere errato o scaduto.</p>
          <Button onClick={() => navigate("/")} data-testid="error-home-cta"
            className="mt-6 rounded-xl h-12 px-6 font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white">
            Controlla un preventivo
          </Button>
        </div>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center">
      <NoIndex />
      <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <NoIndex />
      {data.status === "not_eligible"
        ? <NotEligible eligibility={data.eligibility} />
        : data.status === "preparing"
        ? <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="report-preparing">
            <Loader2 className="w-8 h-8 text-[#3157F6] animate-spin mb-4" />
            <h1 className="font-heading text-xl font-semibold text-[#0F172A]">Stiamo preparando il tuo report completo…</h1>
            <p className="mt-2 text-slate-600">Pagamento ricevuto. Il report si aprirà automaticamente tra pochi secondi.</p>
          </div>
        : data.paid
        ? <ReportView report={data} />
        : data.status && data.status !== "done"
          ? <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-4" />
              <p className="text-slate-600">Stiamo ancora analizzando questo preventivo…</p>
            </div>
          : <Teaser teaser={data.teaser} eligibility={data.eligibility} onUnlock={onUnlock} unlocking={unlocking} />}
    </div>
  );
}
